import { useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import netsuiteLogo from "/img/integration/netsuite_logo.webp"
import { ConnectLogo } from "../connect-logo"
import { finalizeNetsuiteM2m, startNetsuiteM2m } from "@/api/integration"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

interface ConnectNetsuitePageProps {
  returnPath?: string
}

/**
 * NetSuite connect flow using OAuth 2.0 **Machine-to-Machine (client credentials)** — the durable,
 * unattended-sync auth method (the authorization-code flow's refresh tokens expire after ~7 days and
 * require periodic re-consent). NetSuite is account-scoped with no shared Margin Wiz app, so each brand
 * registers its own integration record. Two steps:
 *   1. Brand enters Account ID + Client ID → we generate the signing keypair + certificate.
 *   2. Brand uploads the certificate in NetSuite, gets a Certificate ID, and submits it to finish.
 */
export function ConnectNetsuitePage({ returnPath }: ConnectNetsuitePageProps) {
  const [, setLocation] = useLocation()
  const [account_id, set_account_id] = useState("")
  const [client_id, set_client_id] = useState("")
  const [certificate_pem, set_certificate_pem] = useState<string | null>(null)
  const [certificate_id, set_certificate_id] = useState("")
  const [error, set_error] = useState<string | null>(null)
  const [loading, set_loading] = useState(false)
  const [finalizing, set_finalizing] = useState(false)

  // Default to the current path so it works for both /setup/connect/* and /settings/connect/*.
  const effective_return_path = returnPath ?? window.location.pathname

  async function on_start(e: Event) {
    e.preventDefault()
    if (!account_id.trim() || !client_id.trim()) {
      set_error("Account ID and Client ID are both required.")
      return
    }
    set_loading(true)
    set_error(null)
    const res = await startNetsuiteM2m({ account_id: account_id.trim(), client_id: client_id.trim() })
    set_loading(false)
    if (!res.ok) {
      console.error(res.error)
      set_error("Failed to start the NetSuite connection. Double-check the values and try again.")
      return
    }
    set_certificate_pem(res.value.data.certificate_pem)
  }

  function download_certificate() {
    if (!certificate_pem) {
      return
    }
    const blob = new Blob([certificate_pem], { type: "application/x-pem-file" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "marginwiz-netsuite-certificate.pem"
    a.click()
    URL.revokeObjectURL(url)
  }

  async function on_finalize(e: Event) {
    e.preventDefault()
    if (!certificate_id.trim()) {
      set_error("Enter the Certificate ID from NetSuite to finish connecting.")
      return
    }
    set_finalizing(true)
    set_error(null)
    const res = await finalizeNetsuiteM2m({ certificate_id: certificate_id.trim() })
    set_finalizing(false)
    if (!res.ok) {
      console.error(res.error)
      set_error("Failed to finish the NetSuite connection. Double-check the Certificate ID and try again.")
      return
    }
    setLocation(effective_return_path)
  }

  // Step 2: show the generated certificate for the brand to upload, then capture the Certificate ID.
  if (certificate_pem) {
    return (
      <CenterColumnContainer>
        <div class="space-y-6 max-w-md w-full mx-auto">
          <ConnectLogo src={netsuiteLogo} alt="NetSuite" />

          <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 space-y-2">
            <p class="font-medium text-gray-900">Upload the certificate in NetSuite</p>
            <ol class="list-decimal list-inside space-y-1.5">
              <li>Download the certificate below.</li>
              <li>
                In NetSuite, go to{" "}
                <span class="font-medium">
                  Setup → Integration → OAuth 2.0 Client Credentials (M2M) Setup → Create New
                </span>
                .
              </li>
              <li>
                Select the <span class="font-medium">Application</span> (the integration record you just
                created), the <span class="font-medium">Entity</span> (a user), and a{" "}
                <span class="font-medium">Role</span> that has REST Web Services access.
              </li>
              <li>
                Upload the certificate file, then <span class="font-medium">Save</span>. NetSuite shows a{" "}
                <span class="font-medium">Certificate ID</span> — copy it and paste it below.
              </li>
            </ol>
            <div class="flex items-center gap-2 pt-1">
              <Button type="button" variant="outline" class="text-xs" onClick={download_certificate}>
                Download certificate
              </Button>
              <span class="text-xs text-gray-500">marginwiz-netsuite-certificate.pem</span>
            </div>
          </div>

          <form class="space-y-4" onSubmit={on_finalize}>
            <div class="flex flex-col gap-1.5">
              <Label htmlFor="netsuite-certificate-id">Certificate ID</Label>
              <Input
                id="netsuite-certificate-id"
                value={certificate_id}
                onInput={(e: Event) => set_certificate_id((e.target as HTMLInputElement).value)}
                placeholder="Certificate ID from NetSuite"
                autoFocus
              />
            </div>
            {error && <p class="text-sm text-red-600">{error}</p>}
            <div class="flex justify-between">
              <Button
                type="button"
                variant="ghost"
                class="text-gray-500"
                onClick={() => {
                  set_certificate_pem(null)
                  set_error(null)
                }}>
                Back
              </Button>
              <Button
                type="submit"
                class="bg-gray-900 text-white rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                disabled={finalizing}>
                {finalizing ? "Finishing…" : "Finish connecting"}
              </Button>
            </div>
          </form>
        </div>
      </CenterColumnContainer>
    )
  }

  // Step 1: collect the Account ID + Client ID (the integration record's consumer key).
  return (
    <CenterColumnContainer>
      <div class="space-y-6 max-w-md w-full mx-auto">
        <ConnectLogo src={netsuiteLogo} alt="NetSuite" />

        <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 space-y-2">
          <p class="font-medium text-gray-900">One-time NetSuite setup</p>
          <p class="text-gray-600">
            NetSuite requires each company to register its own connection. In NetSuite (you'll need an
            Administrator), do this once:
          </p>
          <ol class="list-decimal list-inside space-y-1.5">
            <li>
              Enable the features: <span class="font-medium">Setup → Company → Enable Features → SuiteCloud</span>{" "}
              — turn on <span class="font-medium">REST Web Services</span> and{" "}
              <span class="font-medium">OAuth 2.0</span>.
            </li>
            <li>
              Create the integration record:{" "}
              <span class="font-medium">Setup → Integration → Manage Integrations → New</span>. Give it a name
              and set <span class="font-medium">State</span> to <span class="font-medium">Enabled</span>.
            </li>
            <li>
              On the <span class="font-medium">Authentication</span> tab, under{" "}
              <span class="font-medium">OAuth 2.0</span>, check{" "}
              <span class="font-medium">Client Credentials (Machine To Machine) Grant</span> and select the{" "}
              <span class="font-medium">REST Web Services</span> scope.
            </li>
            <li>
              <span class="font-medium">Authorization Code Grant</span> isn't needed for this flow. If you
              leave it checked, NetSuite requires a <span class="font-medium">Redirect URI</span> — enter any
              valid HTTPS URL (e.g. <code class="text-xs">{window.location.origin}</code>); it's never used.
              Leave <span class="font-medium">Token-Based Authentication</span> off.
            </li>
            <li>
              Save. NetSuite shows the <span class="font-medium">Client ID</span> (Consumer Key) — copy it
              and paste it below. (No client secret is needed — you'll upload a certificate in the next step.)
            </li>
          </ol>
          <p class="text-gray-500 text-xs">
            Your Account ID is under <span class="font-medium">Setup → Company → Company Information</span>{" "}
            (e.g. <code class="text-xs">1234567</code>, or <code class="text-xs">1234567_SB1</code> for a
            sandbox). Next, we'll generate a certificate for you to upload in NetSuite's{" "}
            <span class="font-medium">OAuth 2.0 Client Credentials (M2M) Setup</span>.
          </p>
        </div>

        <form class="space-y-4" onSubmit={on_start}>
          <div class="flex flex-col gap-1.5">
            <Label htmlFor="netsuite-account-id">Account ID</Label>
            <Input
              id="netsuite-account-id"
              value={account_id}
              onInput={(e: Event) => set_account_id((e.target as HTMLInputElement).value)}
              placeholder="1234567"
              autoFocus
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <Label htmlFor="netsuite-client-id">Client ID</Label>
            <Input
              id="netsuite-client-id"
              value={client_id}
              onInput={(e: Event) => set_client_id((e.target as HTMLInputElement).value)}
              placeholder="Consumer / Client ID from the integration record"
            />
          </div>
          {error && <p class="text-sm text-red-600">{error}</p>}
          <div class="flex justify-end">
            <Button
              type="submit"
              class="bg-gray-900 text-white rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
              disabled={loading}>
              {loading ? "Generating…" : "Generate certificate"}
            </Button>
          </div>
        </form>
      </div>
    </CenterColumnContainer>
  )
}
